import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  FolderPlus,
  Map as MapIcon,
  Plus,
} from 'lucide-react';
import {
  Button,
  EmptyState,
  Pill,
  SurfacePageBody,
  SurfacePageCard,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageSectionHeader,
  SurfacePageTabs,
  HelpAnchor,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import {
  GLASS_CARD_TILE,
  GLASS_INPUT,
  MENU_CONTENT,
  MENU_ITEM_BASE,
} from '@/shared/ui-class';
import { NewProjectDialog } from '@/versions/v1/chrome/sidebar/NewProjectDialog';
import { useClickOutside } from '@/versions/v1/chrome/sidebar/AccountMenu';
import { PROJECT_COLOR_HEX, type ProjectColor } from '@/versions/v1/shared/project-colors';
import {
  PROJECT_TILES,
  type ProjectTile,
} from '@/versions/v1/shared/project-seed';
import { projectHomeHref } from '@/versions/v1/shared/project-sidebar-nav';
import {
  PORTFOLIO_SORT_LABELS,
  computePortfolioMetrics,
  filterAndSortTiles,
  formatRelativeTime,
  formatViewerRole,
  type PortfolioSortKey,
} from './PortfolioScene.transform';

// PortfolioScene — the `/projects` index (parity.P5). Mirrors ai-foundation's
// ProjectsGrid: metric tiles, a List | Map view toggle (Map is a placeholder),
// search + sort, and the projects table. New projects land in local scene
// state — the mock has no backend.
//
// Spec: docs/logic/workspace/portfolio.md (opens via the ? anchor).

type ViewMode = 'list' | 'map';

const VIEW_TABS = [
  { id: 'list' as const, label: 'List' },
  { id: 'map' as const, label: 'Map' },
];

function SortDropdown({
  sort,
  onChange,
}: {
  sort: PortfolioSortKey;
  onChange: (next: PortfolioSortKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Sort projects"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface)] px-3 text-sm text-zinc-700 transition-colors hover:bg-zinc-900/[0.04] dark:text-zinc-300 dark:hover:bg-white/[0.04]"
      >
        Sort by {PORTFOLIO_SORT_LABELS[sort]}
        <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.6} />
      </button>
      {open ? (
        <div className={cn(MENU_CONTENT, 'absolute right-0 top-full z-30 mt-1.5 w-[200px]')}>
          {(Object.keys(PORTFOLIO_SORT_LABELS) as PortfolioSortKey[]).map(
            (key) => (
              <button
                key={key}
                type="button"
                className={cn(
                  MENU_ITEM_BASE,
                  key === sort &&
                    'font-medium text-zinc-900 dark:text-zinc-100',
                )}
                onClick={() => {
                  onChange(key);
                  setOpen(false);
                }}
              >
                Sort by {PORTFOLIO_SORT_LABELS[key]}
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number;
  sub: string;
}) {
  return (
    <div className={cn('flex h-full flex-col p-5', GLASS_CARD_TILE)}>
      <p className="text-sm font-medium text-muted">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
        {value}
      </p>
      <p className="mt-1 min-h-5 text-sm text-zinc-600 dark:text-zinc-400">{sub}</p>
    </div>
  );
}

const PROJECT_COLORS_CYCLE: ProjectColor[] = ['crimson', 'moss', 'ochre', 'slate'];

export function PortfolioScene() {
  const navigate = useNavigate();
  const [tiles, setTiles] = useState<ProjectTile[]>(PROJECT_TILES);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<PortfolioSortKey>('activity');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showNewProject, setShowNewProject] = useState(false);

  const now = useMemo(() => Date.now(), []);
  const metrics = useMemo(() => computePortfolioMetrics(tiles, now), [tiles, now]);
  const visible = useMemo(
    () => filterAndSortTiles(tiles, query, sort),
    [tiles, query, sort],
  );

  const handleCreateProject = (input: {
    name: string;
    description: string | null;
    color: ProjectColor;
  }) => {
    const slug =
      input.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40) || `project-${tiles.length + 1}`;
    setTiles((prev) => [
      ...prev,
      {
        slug,
        name: input.name,
        description: input.description ?? '',
        color:
          input.color ?? PROJECT_COLORS_CYCLE[prev.length % PROJECT_COLORS_CYCLE.length],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        threadCount: 0,
        fileCount: 0,
        lastActivityAt: null,
        viewerRole: 'owner',
      },
    ]);
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="PortfolioScene">
        <div className="contents">
          <SurfacePageBody width="full" className="space-y-8" as="div">
            <SurfacePageHeader
              kicker="Workspace"
              title="Projects"
              description="Group chats, files, and instructions into scoped workspaces."
              tabs={
                <SurfacePageTabs
                  items={VIEW_TABS}
                  value={viewMode}
                  onChange={setViewMode}
                  aria-label="Projects view"
                />
              }
              actions={
                <HelpAnchor doc="workspace/portfolio" label="Projects portfolio" />
              }
            />

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                label="Open projects"
                value={metrics.totalProjects}
                sub="In your workspace"
              />
              <MetricCard
                label="Active chats"
                value={metrics.activeChats}
                sub="Across all projects"
              />
              <MetricCard
                label="Scoped files"
                value={metrics.scopedFiles}
                sub="Project file library"
              />
              <MetricCard
                label="Updated this week"
                value={metrics.recentlyUpdated}
                sub={
                  metrics.sharedProjects > 0
                    ? `${metrics.sharedProjects} shared with you`
                    : 'Recently touched'
                }
              />
            </div>

            <section className="space-y-4">
              <SurfacePageSectionHeader
                title="Projects"
                meta={`${tiles.length} total`}
                actions={
                  <div className="flex flex-wrap items-center gap-2">
                    <SortDropdown sort={sort} onChange={setSort} />
                    <Button
                      leftIcon={<Plus size={14} aria-hidden />}
                      onClick={() => setShowNewProject(true)}
                    >
                      New project
                    </Button>
                  </div>
                }
              />

              {tiles.length > 0 ? (
                <div className="relative max-w-md">
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search projects…"
                    aria-label="Search projects"
                    className={cn(
                      'h-10 w-full rounded-lg px-3 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-zinc-100 dark:placeholder:text-zinc-500',
                      GLASS_INPUT,
                    )}
                  />
                </div>
              ) : null}

              {tiles.length === 0 ? (
                <EmptyState
                  icon={<FolderPlus className="mx-auto h-8 w-8 text-zinc-400" />}
                  title="No projects yet"
                  description="Group related chats, files, and instructions into a project to keep your context together."
                  action={
                    <Button
                      leftIcon={<Plus size={14} aria-hidden />}
                      onClick={() => setShowNewProject(true)}
                    >
                      New project
                    </Button>
                  }
                />
              ) : viewMode === 'map' ? (
                <SurfacePageCard className="flex flex-col items-center justify-center px-6 py-16 text-center">
                  <MapIcon className="mb-3 h-8 w-8 text-zinc-400" aria-hidden />
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Map view coming soon
                  </p>
                  <p className="mt-1 max-w-sm text-sm text-muted">
                    A spatial overview of your projects will appear here. Switch
                    back to List to browse projects now.
                  </p>
                </SurfacePageCard>
              ) : visible.length === 0 ? (
                <EmptyState
                  icon={<FolderPlus className="mx-auto h-8 w-8 text-zinc-400" />}
                  title="No matching projects"
                  description="Try a different search term."
                />
              ) : (
                <SurfacePageCard className="overflow-hidden p-0">
                  <Table clickableRows wrap={false} className="min-w-[56rem] text-left">
                    <THead className="border-b border-[var(--glass-border)] bg-transparent">
                      <TR>
                        <TH className="px-5 py-3">Project</TH>
                        <TH>Stage</TH>
                        <TH>Gates</TH>
                        <TH>Readiness</TH>
                        <TH>Owner</TH>
                        <TH>Budget</TH>
                        <TH>Updated</TH>
                      </TR>
                    </THead>
                    <TBody className="divide-y divide-[var(--glass-border)]">
                      {visible.map((tile) => (
                        <TR
                          key={tile.slug}
                          className="border-0 hover:bg-zinc-900/[0.03] dark:hover:bg-white/[0.03]"
                          onRowClick={() => navigate(projectHomeHref(tile.slug))}
                        >
                          <TD className="px-5 py-3.5">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span
                              aria-hidden
                              className="h-2.5 w-2.5 flex-shrink-0 rounded-full ring-1 ring-inset ring-black/10"
                              style={{
                                backgroundColor: PROJECT_COLOR_HEX[tile.color],
                              }}
                            />
                            <div className="min-w-0">
                              <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">
                                {tile.name}
                              </p>
                              {tile.description ? (
                                <p className="mt-0.5 truncate text-xs text-muted">
                                  {tile.description}
                                </p>
                              ) : null}
                            </div>
                            {tile.viewerRole !== 'owner' ? (
                              <Pill className="flex-shrink-0">Shared</Pill>
                            ) : null}
                          </div>
                        </TD>
                        <TD className="py-3.5 text-muted">—</TD>
                        <TD className="py-3.5 text-muted">—</TD>
                        <TD className="py-3.5 text-zinc-700 dark:text-zinc-300">
                          {tile.threadCount} chat{tile.threadCount === 1 ? '' : 's'}
                          <span className="text-muted"> · </span>
                          {tile.fileCount} file{tile.fileCount === 1 ? '' : 's'}
                        </TD>
                        <TD className="py-3.5 text-zinc-700 dark:text-zinc-300">
                          {formatViewerRole(tile.viewerRole)}
                        </TD>
                        <TD className="py-3.5 text-muted">—</TD>
                        <TD className="whitespace-nowrap py-3.5 text-muted">
                          {formatRelativeTime(
                            tile.lastActivityAt ?? tile.updatedAt,
                            now,
                          )}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
                </SurfacePageCard>
              )}
            </section>
          </SurfacePageBody>
        </div>

        <NewProjectDialog
          open={showNewProject}
          onOpenChange={setShowNewProject}
          onSubmit={handleCreateProject}
        />
      </Commentable>
    </SurfacePageLayout>
  );
}
